import { useEffect, useState } from "react";
import moment from "moment";
import { Pagination } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContentContainer from "@/components/containers/contentContainer";
import { api } from "@/utils/api";
import JobSkeleton from "@/utils/jobskeleton";

const isOpenRequestQuoteJob = (job: any) =>
  job?.isProposalSent === false || job?.isProposalSent === "false";

const getServiceName = (job: any) => {
  if (typeof job?.service === "object" && job.service?.name) return job.service.name;
  if (typeof job?.service === "string") return job.service;
  return "-";
};

/** Format as received/total e.g. "1/3" — proposals received vs providers the quote was sent to. */
const getProposalProgressLabel = (job: any): string => {
  const toCount = (raw: unknown): number | null => {
    if (typeof raw === "number" && Number.isFinite(raw)) return Math.max(0, raw);
    if (typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw))) {
      return Math.max(0, Number(raw));
    }
    return null;
  };

  const received =
    toCount(job?.proposalCount) ??
    toCount(job?.proposalsCount) ??
    toCount(job?.totalProposals) ??
    (Array.isArray(job?.proposals) ? job.proposals.length : 0);

  const total =
    toCount(job?.sentCount) ??
    toCount(job?.recipientCount) ??
    toCount(job?.providersSentCount) ??
    toCount(job?.totalRecipients) ??
    null;

  if (total == null) return String(received);
  return `${received}/${total}`;
};

const RequestQuotesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: { user: any }) => state.user);

  const [jobs, setJobs] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [fetching, setFetching] = useState(false);

  const fetchRequestQuotes = async (pageNum = page) => {
    if (!user?._id) return;
    setFetching(true);
    try {
      const res = await api.getMyJobs({
        userId: user._id,
        type: "hire",
        isProposalSent: false,
        page: pageNum,
        limit: pageSize,
      });
      if (res?.success) {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setJobs(rows.filter(isOpenRequestQuoteJob));
        setTotalPages(res.data?.totalPages || 1);
      } else {
        setJobs([]);
        setTotalPages(1);
      }
    } catch {
      setJobs([]);
      setTotalPages(1);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchRequestQuotes(1);
  }, [user?._id]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchRequestQuotes(newPage);
  };

  const openProposals = (job: any) => {
    const id = job?._id;
    if (!id) return;
    navigate(`/request-quotes/${id}`, {
      state: {
        job: {
          _id: job._id,
          uid: job.uid,
          createdAt: job.createdAt,
          service: job.service,
          description: job.description,
        },
      },
    });
  };

  return (
    <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen">
      <div className="px-4 sm:px-6 pt-6 pb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-[#111c2d]">
          {t("requestQuotes")}
        </h1>
        <p className="text-sm text-[#424654] mt-1">
          {t("requestQuotesSubtitle") ||
            "Open quote requests waiting for professional proposals."}
        </p>
      </div>

      <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
        {!fetching &&
          jobs.map((job: any) => (
            <div
              key={job._id}
              className="bg-white rounded-xl border border-[#d8e3fb] shadow-sm p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6"
            >
              <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 xl:gap-5">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#424654]">
                    {t("jobId")}
                  </p>
                  <p
                    className="text-sm font-semibold text-[#111c2d] truncate mt-0.5"
                    title={job.uid || job._id}
                  >
                    {job.uid || job._id}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#424654]">
                    {t("service_name")}
                  </p>
                  <p
                    className="text-sm text-[#111c2d] truncate mt-0.5"
                    title={getServiceName(job)}
                  >
                    {getServiceName(job)}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#424654]">
                    {t("proposedDate")}
                  </p>
                  <p className="text-sm text-[#111c2d] mt-0.5 whitespace-nowrap">
                    {moment(job.createdAt).format("DD MMM YYYY")}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#424654]">
                    {t("proposals")}
                  </p>
                  <p className="text-sm font-semibold text-[#111c2d] mt-0.5">
                    {getProposalProgressLabel(job)}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#424654]">
                    {t("status")}
                  </p>
                  <span className="inline-flex items-center gap-1.5 mt-1 rounded-full bg-[#e7eeff] px-3 py-1 text-xs font-semibold text-[#4285F4]">
                    <span className="size-1.5 rounded-full bg-[#4285F4]" />
                    {t("awaitingProposals")}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => openProposals(job)}
                className="h-10 shrink-0 w-full lg:w-auto px-5 rounded-lg bg-[#4285F4] text-white text-sm font-semibold shadow-sm hover:bg-[#4285F4] transition-colors whitespace-nowrap"
              >
                {t("view_proposals")}
              </button>
            </div>
          ))}

        {!fetching && jobs.length === 0 && (
          <div className="bg-white rounded-xl border border-[#d8e3fb] px-6 py-12 text-center">
            <p className="text-sm text-[#424654]">{t("noRequestQuotesYet")}</p>
          </div>
        )}

        {fetching && (
          <div className="flex flex-col gap-4">
            {[...Array(4)].map((_, i) => (
              <JobSkeleton key={i} />
            ))}
          </div>
        )}

        <div className="flex justify-center pt-2">
          <Pagination
            count={totalPages}
            color="primary"
            variant="outlined"
            page={page}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
            siblingCount={0}
          />
        </div>
      </div>
    </ContentContainer>
  );
};

export default RequestQuotesPage;
